import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Target, Plus, Trash2, RefreshCw, ArrowRight } from "lucide-react";
import { getAuthHeaders } from "../services/authHeaders";

const XANO_BASE_URL = "https://api.sobevenda.com.br/api-proxy/affiliate";

// Helper local — pode ser movido para src/utils/array.js
function toArray(data, ...possibleKeys) {
  if (Array.isArray(data)) return data;
  for (const key of possibleKeys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  return [];
}

function fmtDate(dt) {
  if (!dt) return "-";
  try {
    return new Date(dt).toLocaleString("pt-BR");
  } catch {
    return String(dt);
  }
}

export default function GoogleAdsConfig() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const callbackError = searchParams.get("error");

  const loadAccounts = async () => {
    setLoading(true);
    setError("");

    const token = localStorage.getItem("sobevenda_token");
    if (!token) {
      setError("Token de autenticação não encontrado.");
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${XANO_BASE_URL}/google-ads-accounts`, {
        headers: getAuthHeaders(),
      });
      setAccounts(toArray(res.data, "accounts", "items", "data"));
    } catch {
      setError("Erro ao carregar contas. Verifique sua conexão e tente novamente.");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja remover esta conta?")) return;
    try {
      await axios.delete(`${XANO_BASE_URL}/google-ads-accounts/${id}`, {
        headers: getAuthHeaders(),
      });
      setAccounts((prev) => prev.filter((acc) => acc.id !== id));
    } catch {
      setError("Erro ao apagar conta. Tente novamente.");
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto  space-y-6">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Target className="w-6 h-6" />
              Contas do Google Ads
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gerencie as contas do Google Ads conectadas ao SobeVenda para automação de conversões.
            </p>
          </div>

          <button
            onClick={loadAccounts}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 shrink-0 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>

        {/* ERROS */}
        {callbackError ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">
            {decodeURIComponent(callbackError)}
          </div>
        ) : null}

        {error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 border border-red-200 text-sm dark:bg-red-900/20 dark:text-red-400 dark:border-red-900">
            {error}
          </div>
        ) : null}

        {/* AÇÃO PRIMÁRIA */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {loading
              ? "Carregando contas..."
              : `${accounts.length} ${accounts.length === 1 ? "conta conectada" : "contas conectadas"}`}
          </p>

          <Link
            to="/connect-google-account"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            <Plus className="w-4 h-4" />
            Adicionar conta
          </Link>
        </div>

        {/* LISTAGEM — CARDS NO MOBILE / TABELA NO DESKTOP */}

        {/* Mobile/Tablet: Cards (visível < lg) */}
        <div className="lg:hidden space-y-3">
          {loading ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500 text-sm">
              Carregando contas conectadas...
            </div>
          ) : accounts.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 text-slate-500 text-sm">
              Nenhuma conta conectada ainda.
            </div>
          ) : (
            accounts.map((acc) => (
              <div
                key={acc.id}
                className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
              >
                {/* Cabeçalho do card: e-mail */}
                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                    E-mail
                  </div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100 break-all">
                    {acc.email || "-"}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">
                    Conectada em
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-300">
                    {fmtDate(acc.created_at)}
                  </div>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                  <Link
                    to={`/google-ads/${acc.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                  >
                    Ver dados
                    <ArrowRight className="w-4 h-4" />
                  </Link>

                  <button
                    onClick={() => handleDelete(acc.id)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remover
                  </button>
                </div>

                <div className="text-[11px] text-slate-400 text-right mt-2">
                  ID: {acc.id}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop: Tabela (visível >= lg) */}
        <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/40">
                <tr className="text-left">
                  <th className="px-4 py-3 w-[45%] font-semibold text-slate-700 dark:text-slate-200">
                    E-mail
                  </th>
                  <th className="px-4 py-3 w-[25%] font-semibold text-slate-700 dark:text-slate-200">
                    Conectada em
                  </th>
                  <th className="px-4 py-3 w-[30%] text-right font-semibold text-slate-700 dark:text-slate-200">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td className="px-4 py-4 text-slate-500 text-sm" colSpan={3}>
                      Carregando contas conectadas...
                    </td>
                  </tr>
                ) : accounts.length === 0 ? (
                  <tr>
                    <td className="px-4 py-4 text-slate-500 text-sm" colSpan={3}>
                      Nenhuma conta conectada ainda.
                    </td>
                  </tr>
                ) : (
                  accounts.map((acc) => (
                    <tr
                      key={acc.id}
                      className="border-t border-slate-100 dark:border-slate-800 align-top"
                    >
                      <td className="px-4 py-3 text-slate-900 dark:text-slate-100 break-all">
                        {acc.email || "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                        {fmtDate(acc.created_at)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/google-ads/${acc.id}`}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm"
                          >
                            Ver dados
                            <ArrowRight className="w-4 h-4" />
                          </Link>

                          <button
                            onClick={() => handleDelete(acc.id)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            Remover
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}